/**
 * Pickeo por escaneo agrupado por lote — lógica pura.
 *
 * Modelo (requerimiento del 2026-09-14):
 *   - El detalle del despacho se agrupa por artículo y lote, con la cantidad
 *     esperada de unidades de cada grupo (`agruparDetalle`).
 *   - El modal arranca vacío y se va armando con lo que se pickea: cada lectura
 *     válida suma una unidad al grupo (artículo, lote) al que pertenece. Solo se
 *     acepta si ese artículo y lote existen en el detalle original.
 *   - La serie no se coteja contra una lista (el ERP puede no informarla); se
 *     registra y se usa para rechazar una misma unidad escaneada dos veces.
 *   - Un grupo no admite más unidades que las que declara el detalle.
 *
 * Lecturas aceptadas:
 *   - String GS1 del DataMatrix: GTIN, lote, vencimiento y serie. Si falta el
 *     separador entre lote y serie, el lote se resuelve contra los lotes del
 *     detalle (`10<lote>21<serie>` o `21<serie>10<lote>`). Se tolera un
 *     carácter sustituto del separador entre ambos (`10<lote>+21<serie>`):
 *     algunos lectores mandan una tecla en lugar del GS. Si ningún lote lo
 *     explica, se informa que la lectura llegó sin separador.
 *   - Número de serie suelto: tipeado a mano, o leído de un código lineal que
 *     trae solo el AI 21 (`21<serie>`, con o sin paréntesis). Solo sirve si el
 *     detalle informa esa serie, porque es la única forma de saber a qué
 *     artículo y lote va; si no, se avisa que hay que escanear el DataMatrix.
 *   - Código de barras del envase (EAN-13, UPC, GTIN-14): trae el GTIN del
 *     producto y nunca la unidad. Se reconoce y se rechaza con un mensaje que
 *     dice qué producto es y que hay que escanear el DataMatrix.
 *
 * Sin React, sin fetch, sin storage — para poder testearla con Vitest.
 */

import type { ComprobantePartida, PickeoRegistrado } from '@/api/contracts/comprobantes';
import {
  gtinNormalizado,
  pareceLecturaGs1,
  parsearStringGs1,
  validarGtin14,
  vencimientoGs1AMesAnio,
} from '@/domain/gs1';

/** Misma regla que `esPartidaTrazable` del backend (traza.service.js). */
export function esPartidaTrazable(p: ComprobantePartida): boolean {
  if (p.trazable == null) return Boolean(p.numeroDeSerie);
  return p.trazable;
}

function normalizar(v: string | null | undefined): string {
  return (v ?? '').trim();
}

/**
 * Partida trazable que agrupa varias unidades de un lote sin informar sus
 * series: el formato por lote de ACSIS. Las series surgen del pickeo, así que
 * todavía no tiene stock asociado.
 */
export function esPartidaPorLote(p: ComprobantePartida): boolean {
  return esPartidaTrazable(p) && !normalizar(p.numeroDeSerie);
}

/**
 * Unidades que representa una partida trazable: una si trae serie (formato
 * por serie); si agrupa el lote, `cantidad` (lo que sale en el remito). Una
 * partida por lote sin cantidad cuenta como 0: no deja pickear hasta que se
 * corrija en Plataforma. `cantidadInicial` no sirve acá: es la cantidad de
 * alta del lote en ACSIS.
 */
export function unidadesDePartida(p: ComprobantePartida): number {
  if (!esPartidaPorLote(p)) return 1;
  if (p.cantidad == null) return 0;
  return Math.max(0, Math.round(p.cantidad));
}

/** Clave del grupo (lote, artículo) al que pertenece una partida. Null sin lote. */
export function claveGrupo(p: ComprobantePartida): string | null {
  const lote = normalizar(p.lote);
  if (!lote) return null;
  return `${lote}|${gtinNormalizado(p.gtin) ?? normalizar(p.sku)}`;
}

// =============================================================================
// Detalle agrupado
// =============================================================================

/** Un artículo dentro de un lote del detalle, con su cantidad esperada. */
export interface GrupoEsperado {
  /** `${lote}|${gtin ?? sku}` — identifica el par (lote, artículo). */
  key: string;
  lote: string;
  /** Fecha de vencimiento cruda del ERP (`YYYY-MM-DD …`). */
  vencimiento: string | null;
  /** GTIN del maestro normalizado a 14 dígitos. Null si el ERP no lo tiene. */
  gtin: string | null;
  sku: string | null;
  nombre: string | null;
  /** Unidades esperadas: una por partida con serie, `cantidadInicial` por partida de lote. */
  cantidad: number;
  /** Series que el ERP declaró para el grupo (puede estar vacío). */
  series: readonly string[];
}

export function agruparDetalle(partidas: readonly ComprobantePartida[]): GrupoEsperado[] {
  const grupos = new Map<string, GrupoEsperado & { series: string[] }>();
  for (const p of partidas) {
    if (!esPartidaTrazable(p)) continue;
    const key = claveGrupo(p);
    if (!key) continue; // sin lote no hay grupo posible
    const lote = normalizar(p.lote);
    const gtin = gtinNormalizado(p.gtin) ?? null;
    let g = grupos.get(key);
    if (!g) {
      g = {
        key,
        lote,
        vencimiento: p.fechaVencimiento ?? null,
        gtin,
        sku: p.sku ?? null,
        nombre: p.nombre ?? null,
        cantidad: 0,
        series: [],
      };
      grupos.set(key, g);
    }
    g.cantidad += unidadesDePartida(p);
    const serie = normalizar(p.numeroDeSerie);
    if (serie) g.series.push(serie);
  }
  return Array.from(grupos.values());
}

// =============================================================================
// Interpretación de la lectura
// =============================================================================

export interface LecturaPickeo {
  esGs1: boolean;
  /** La lectura no trae GTIN ni lote: solo una serie (tipeada o de un código lineal con AI 21). */
  soloSerie: boolean;
  /** La lectura es el código de barras del envase: trae el GTIN del producto, no una unidad. */
  soloGtin: boolean;
  /** GS1 sin separador entre lote y serie, y ningún lote del detalle explica la cola. */
  sinSeparador: boolean;
  gtin: string | null;
  lote: string | null;
  /** `YYMMDD` del AI 17. */
  vencimiento: string | null;
  serie: string | null;
}

function gtinCompatible(a: string | null, b: string | null): boolean {
  if (!a || !b) return true;
  return (gtinNormalizado(a) ?? a) === (gtinNormalizado(b) ?? b);
}

/**
 * Un carácter que un lector manda en lugar del GS (FNC1). La Zebra DS3678 del
 * laboratorio manda la tecla `Shift`+`Equal`, que es `+` en la tabla US. Se
 * acepta cualquier carácter no alfanumérico, siempre anclado a un lote del ERP
 * que coincida exacto: sin ese anclaje sería adivinar.
 */
function esSustitutoDeSeparador(ch: string | undefined): boolean {
  return ch != null && ch.length === 1 && !/[0-9A-Za-z]/.test(ch);
}

/** Quita un sustituto de separador al final de la serie (GS de cierre). */
function limpiarSerie(serie: string): string | null {
  const s = esSustitutoDeSeparador(serie.at(-1)) ? serie.slice(0, -1) : serie;
  return s || null;
}

/**
 * Resuelve lote y serie de una cola GS1 sin separador usando los lotes del
 * detalle. Se prueban los lotes más largos primero para que un lote que es
 * prefijo de otro no gane por error. Entre el lote y el `21` de la serie se
 * tolera un carácter sustituto del separador.
 */
function resolverColaConLotes(
  cola: string,
  gtin: string | null,
  grupos: readonly GrupoEsperado[],
): { lote: string; serie: string | null } | null {
  const lotes = Array.from(
    new Set(grupos.filter((g) => gtinCompatible(gtin, g.gtin)).map((g) => g.lote)),
  ).sort((a, b) => b.length - a.length);
  for (const lote of lotes) {
    const l = `10${lote}`;
    if (cola === l) return { lote, serie: null };
    if (cola.startsWith(l)) {
      let resto = cola.slice(l.length);
      if (esSustitutoDeSeparador(resto[0])) resto = resto.slice(1);
      if (resto === '') return { lote, serie: null };
      if (resto.startsWith('21')) return { lote, serie: limpiarSerie(resto.slice(2)) };
    }
    if (cola.startsWith('21') && cola.endsWith(l) && cola.length > l.length + 2) {
      return { lote, serie: limpiarSerie(cola.slice(2, cola.length - l.length)) };
    }
  }
  return null;
}

/** Candidatos a serie de un texto suelto: entero y, si empieza con `21`, sin ese prefijo. */
function candidatosSerie(texto: string): string[] {
  const candidatos = [texto];
  if (/^21.+/.test(texto)) candidatos.push(texto.slice(2));
  return candidatos;
}

/** Primer candidato que el detalle declara como serie, con su grupo. */
function ubicarPorSerie(
  candidatos: readonly string[],
  grupos: readonly GrupoEsperado[],
): { grupo: GrupoEsperado; serie: string } | null {
  for (const serie of candidatos) {
    const grupo = grupos.find((g) => g.series.includes(serie));
    if (grupo) return { grupo, serie };
  }
  return null;
}

/**
 * GTIN de un código de barras de producto (EAN-13, UPC-A, GTIN-14), o null.
 * Se acepta si coincide con el GTIN de un artículo del detalle, o si tiene 12
 * a 14 dígitos con dígito verificador válido. EAN-8 queda afuera a propósito:
 * ocho dígitos es un largo típico de serie y el verificador acierta por azar
 * una de cada diez veces.
 */
function gtinDeCodigoProducto(texto: string, grupos: readonly GrupoEsperado[]): string | null {
  const g = gtinNormalizado(texto);
  if (!g) return null;
  if (grupos.some((x) => x.gtin === g)) return g;
  return /^\d{12,14}$/.test(texto) && validarGtin14(g) ? g : null;
}

export function interpretarLecturaPickeo(
  raw: string,
  grupos: readonly GrupoEsperado[],
): LecturaPickeo {
  const texto = normalizar((raw ?? '').replace(/[\r\n]+$/g, ''));

  if (!pareceLecturaGs1(texto)) {
    // Serie suelta: solo se ubica si el detalle la declara. Un código lineal
    // con el AI 21 llega como `21<serie>`: se prueba primero el texto entero
    // (una serie puede empezar con 21) y después sin el prefijo.
    const ubicada = ubicarPorSerie(candidatosSerie(texto), grupos);
    if (ubicada) {
      return {
        esGs1: false,
        soloSerie: true,
        soloGtin: false,
        sinSeparador: false,
        gtin: ubicada.grupo.gtin,
        lote: ubicada.grupo.lote,
        vencimiento: null,
        serie: ubicada.serie,
      };
    }
    // Código de barras del envase: identifica el producto, no la unidad.
    const gtinProducto = gtinDeCodigoProducto(texto, grupos);
    if (gtinProducto) {
      return {
        esGs1: false,
        soloSerie: false,
        soloGtin: true,
        sinSeparador: false,
        gtin: gtinProducto,
        lote: null,
        vencimiento: null,
        serie: null,
      };
    }
    return {
      esGs1: false,
      soloSerie: true,
      soloGtin: false,
      sinSeparador: false,
      gtin: null,
      lote: null,
      vencimiento: null,
      serie: texto || null,
    };
  }

  const p = parsearStringGs1(texto);
  let lote = p.lote;
  let serie = p.serie;
  let sinSeparador = false;
  if (p.colaSinSeparador) {
    const r = resolverColaConLotes(p.colaSinSeparador, p.gtin, grupos);
    if (r) {
      lote = r.lote;
      // Si la cola era solo `10<lote>`, la serie ya venía delimitada antes.
      if (r.serie != null || p.colaSinSeparador !== `10${r.lote}`) serie = r.serie;
    } else if (p.serie == null) {
      // El lote absorbió todo y ningún lote conocido lo explica: el valor
      // tentativo mezcla lote y serie, así que no sirve ninguno de los dos.
      lote = null;
      serie = null;
      sinSeparador = true;
    }
    // Si `p.serie` venía delimitado y la cola era solo la serie final, el lote
    // ya estaba bien separado: se conservan ambos tal como vinieron.
  }
  // GS1 con solo el AI 21 (código lineal `(21)…` o `]C121…`): sin GTIN ni lote,
  // la única forma de ubicarlo es una serie declarada por el detalle.
  const soloSerie = !p.gtin && !lote;
  if (soloSerie && serie) {
    const ubicada = ubicarPorSerie([normalizar(serie)], grupos);
    if (ubicada) {
      return {
        esGs1: true,
        soloSerie,
        soloGtin: false,
        sinSeparador: false,
        gtin: ubicada.grupo.gtin,
        lote: ubicada.grupo.lote,
        vencimiento: p.vencimiento,
        serie: ubicada.serie,
      };
    }
  }
  return {
    esGs1: p.esGs1,
    soloSerie,
    soloGtin: false,
    sinSeparador,
    gtin: p.gtin,
    lote: lote ? normalizar(lote) : null,
    vencimiento: p.vencimiento,
    serie: serie ? normalizar(serie) : null,
  };
}

// =============================================================================
// Estado
// =============================================================================

export interface Pickeo {
  serie: string;
  grupoKey: string;
  ts: string;
  lectura: LecturaPickeo;
  /**
   * Fila de `stock` que guardó el back. `undefined`/null = todavía no
   * confirmada (el registro está en curso).
   */
  stockId?: number | null;
}

export type TipoResultadoPickeo =
  | 'pickeada'
  | 'duplicada'
  | 'articulo_ajeno'
  | 'lote_ajeno'
  | 'solo_serie'
  | 'solo_gtin'
  | 'sin_separador'
  | 'excedente'
  | 'sin_serie'
  | 'ilegible'
  | 'vacia'
  /** El back rechazó la unidad al guardarla (serie reservada por otro remito, egresada, etc.). */
  | 'rechazada';

export interface ResultadoPickeo {
  tipo: TipoResultadoPickeo;
  mensaje: string;
  lectura: LecturaPickeo | null;
  /** Grupo afectado (pickeada / duplicada / excedente). */
  grupoKey?: string;
}

export interface LecturaRechazada {
  texto: string;
  motivo: string;
  ts: string;
}

export interface PickeoLotesState {
  pickeos: readonly Pickeo[];
  rechazadas: readonly LecturaRechazada[];
  ultimoResultado: ResultadoPickeo | null;
}

export function estadoInicialPickeo(): PickeoLotesState {
  return { pickeos: [], rechazadas: [], ultimoResultado: null };
}

function contarEnGrupo(state: PickeoLotesState, grupoKey: string): number {
  return state.pickeos.filter((p) => p.grupoKey === grupoKey).length;
}

export function procesarPickeo(
  state: PickeoLotesState,
  raw: string,
  grupos: readonly GrupoEsperado[],
  ahora: () => string = () => new Date().toISOString(),
): { state: PickeoLotesState; resultado: ResultadoPickeo } {
  const texto = normalizar(raw);
  const rechazar = (
    tipo: TipoResultadoPickeo,
    mensaje: string,
    lectura: LecturaPickeo | null,
    anotar: boolean,
    grupoKey?: string,
  ) => {
    const resultado: ResultadoPickeo = { tipo, mensaje, lectura, grupoKey };
    return {
      state: {
        ...state,
        rechazadas: anotar
          ? [...state.rechazadas, { texto, motivo: mensaje, ts: ahora() }]
          : state.rechazadas,
        ultimoResultado: resultado,
      },
      resultado,
    };
  };

  if (!texto) {
    return rechazar('vacia', 'Lectura vacía.', null, false);
  }

  const lectura = interpretarLecturaPickeo(texto, grupos);

  if (lectura.soloGtin) {
    // Código de barras del envase (EAN-13 y similares): dice qué producto es,
    // no qué unidad. El mensaje nombra el artículo si está en el despacho.
    const articulo = grupos.find((g) => g.gtin === lectura.gtin);
    const que = articulo
      ? `Ese es el código de barras del producto ${articulo.nombre ?? articulo.sku ?? ''}`.trim()
      : `El código leído es el de barras de un producto (GTIN ${lectura.gtin ?? '?'})`;
    return rechazar(
      'solo_gtin',
      `${que}. No identifica la unidad: escaneá el DataMatrix.`,
      lectura,
      true,
    );
  }
  if (lectura.soloSerie && !lectura.lote) {
    // Solo una serie (tipeada, o un código lineal con el AI 21) que el detalle
    // no declara: sin GTIN ni lote no hay forma de ubicarla.
    return rechazar(
      'solo_serie',
      'La lectura solo trae el número de serie y este despacho no lo declara. Escaneá el DataMatrix de la unidad.',
      lectura,
      true,
    );
  }
  if (lectura.sinSeparador) {
    // El lote absorbió la serie y ningún lote del despacho explica la cola.
    // Puede ser una unidad ajena o un lector que no transmite el separador:
    // el mensaje dice qué pasó con la lectura, no adivina la causa.
    return rechazar(
      'sin_separador',
      'La lectura llegó sin el separador entre lote y serie, y ningún lote de este despacho la explica. Si la unidad es de este despacho, el lector no está transmitiendo el separador.',
      lectura,
      true,
    );
  }
  // Unidad que no es del pedido: se informa un solo motivo. Si el artículo
  // (GTIN) no está, eso; si está pero no con ese lote, el lote.
  const rechazarAjena = () =>
    grupos.some((g) => gtinCompatible(lectura.gtin, g.gtin))
      ? rechazar('lote_ajeno', 'Lote no encontrado en el pedido.', lectura, true)
      : rechazar('articulo_ajeno', 'Artículo no encontrado en el pedido.', lectura, true);

  if (lectura.esGs1 && !lectura.lote) {
    // GS1 con GTIN pero sin lote delimitable por otra causa (AI desconocido,
    // dato truncado): no es de este despacho o el código está dañado.
    return rechazarAjena();
  }

  const grupo = grupos.find((g) => g.lote === lectura.lote && gtinCompatible(lectura.gtin, g.gtin));
  if (!grupo) {
    return rechazarAjena();
  }
  if (!lectura.serie) {
    return rechazar('sin_serie', 'La lectura no trae número de serie.', lectura, true, grupo.key);
  }
  if (state.pickeos.some((p) => p.serie === lectura.serie)) {
    return rechazar(
      'duplicada',
      `La serie ${lectura.serie} ya fue pickeada.`,
      lectura,
      false,
      grupo.key,
    );
  }
  const yaPickeadas = contarEnGrupo(state, grupo.key);
  if (yaPickeadas >= grupo.cantidad) {
    return rechazar(
      'excedente',
      `Lote ${grupo.lote} completo: ya se pickearon ${yaPickeadas} de ${grupo.cantidad}.`,
      lectura,
      true,
      grupo.key,
    );
  }

  const resultado: ResultadoPickeo = {
    tipo: 'pickeada',
    mensaje: `Pickeada · ${grupo.nombre ?? grupo.sku ?? 'artículo'} · lote ${grupo.lote} · ${yaPickeadas + 1} de ${grupo.cantidad}`,
    lectura,
    grupoKey: grupo.key,
  };
  return {
    state: {
      ...state,
      pickeos: [
        ...state.pickeos,
        { serie: lectura.serie, grupoKey: grupo.key, ts: ahora(), lectura },
      ],
      ultimoResultado: resultado,
    },
    resultado,
  };
}

/** Deshace el pickeo de una serie. */
export function quitarPickeo(state: PickeoLotesState, serie: string): PickeoLotesState {
  if (!state.pickeos.some((p) => p.serie === serie)) return state;
  return {
    ...state,
    pickeos: state.pickeos.filter((p) => p.serie !== serie),
    ultimoResultado: null,
  };
}

/**
 * Pickeos guardados en el back → pickeos del estado. El grupo sale de la
 * partida a la que quedó atada la unidad; si no está en el detalle, de su
 * lote y GTIN.
 */
export function pickeosDesdeGuardados(
  guardados: readonly PickeoRegistrado[],
  partidas: readonly ComprobantePartida[],
): Pickeo[] {
  const partidaPorId = new Map(partidas.filter((p) => p.id != null).map((p) => [p.id, p]));
  return guardados.map((g) => {
    const partida = g.erpTaskDetailId != null ? partidaPorId.get(g.erpTaskDetailId) : undefined;
    const grupoKey =
      (partida && claveGrupo(partida)) ??
      `${normalizar(g.lote)}|${gtinNormalizado(g.gtin) ?? normalizar(g.sku)}`;
    return {
      serie: g.serie,
      grupoKey,
      ts: g.pickeadoAt ?? new Date(0).toISOString(),
      stockId: g.stockId,
      lectura: {
        esGs1: true,
        soloSerie: false,
        soloGtin: false,
        sinSeparador: false,
        gtin: g.gtin,
        lote: g.lote,
        vencimiento: null,
        serie: g.serie,
      },
    };
  });
}

/**
 * Estado con los pickeos del back más los que todavía se están guardando
 * (sin `stockId`). Lo guardado manda: una serie confirmada reemplaza a la
 * local pendiente.
 */
export function fusionarPickeos(
  state: PickeoLotesState,
  confirmados: readonly Pickeo[],
): PickeoLotesState {
  const series = new Set(confirmados.map((p) => p.serie));
  const pendientes = state.pickeos.filter((p) => p.stockId == null && !series.has(p.serie));
  return { ...state, pickeos: [...confirmados, ...pendientes] };
}

/** Marca como guardada una unidad pickeada (le asigna la fila de stock). */
export function confirmarPickeo(
  state: PickeoLotesState,
  serie: string,
  stockId: number,
): PickeoLotesState {
  return {
    ...state,
    pickeos: state.pickeos.map((p) => (p.serie === serie ? { ...p, stockId } : p)),
  };
}

/** El back rechazó la unidad: se saca del pickeo y queda como último resultado. */
export function rechazarPickeo(
  state: PickeoLotesState,
  serie: string,
  mensaje: string,
  ahora: () => string = () => new Date().toISOString(),
): PickeoLotesState {
  const quitada = state.pickeos.find((p) => p.serie === serie);
  const resultado: ResultadoPickeo = {
    tipo: 'rechazada',
    mensaje,
    lectura: quitada?.lectura ?? null,
    grupoKey: quitada?.grupoKey,
  };
  return {
    pickeos: state.pickeos.filter((p) => p.serie !== serie),
    rechazadas: [...state.rechazadas, { texto: serie, motivo: mensaje, ts: ahora() }],
    ultimoResultado: resultado,
  };
}

/** Resultados que identifican la unidad: la interpretación del texto fue correcta. */
const TIPOS_IDENTIFICADA: ReadonlySet<TipoResultadoPickeo> = new Set([
  'pickeada',
  'duplicada',
  'excedente',
]);

/**
 * Procesa una lectura probando varias interpretaciones del mismo texto (la
 * principal y las `alternativas` que arma el campo de lectura según la
 * distribución de teclado): la primera que identifica la unidad gana. Si
 * ninguna lo hace, gana la primera que al menos reconoció artículo y lote
 * (`sin_serie`); si tampoco, el resultado de la principal. `procesarPickeo`
 * es pura, así que probar varias no deja rastro.
 */
export function procesarPickeoConAlternativas(
  state: PickeoLotesState,
  textos: readonly string[],
  grupos: readonly GrupoEsperado[],
  ahora: () => string = () => new Date().toISOString(),
): { state: PickeoLotesState; resultado: ResultadoPickeo; textoUsado: string } {
  const lista = textos.length > 0 ? textos : [''];
  const resultados = lista.map((texto) => ({
    texto,
    ...procesarPickeo(state, texto, grupos, ahora),
  }));
  const elegido =
    resultados.find((r) => TIPOS_IDENTIFICADA.has(r.resultado.tipo)) ??
    resultados.find((r) => r.resultado.tipo === 'sin_serie') ??
    resultados[0];
  if (!elegido) throw new Error('procesarPickeoConAlternativas: sin resultados');
  return { state: elegido.state, resultado: elegido.resultado, textoUsado: elegido.texto };
}

// =============================================================================
// Vistas derivadas
// =============================================================================

export interface ResumenPickeo {
  esperadas: number;
  pickeadas: number;
  completo: boolean;
}

export function resumirPickeo(
  state: PickeoLotesState,
  grupos: readonly GrupoEsperado[],
): ResumenPickeo {
  const esperadas = grupos.reduce((s, g) => s + g.cantidad, 0);
  const pickeadas = state.pickeos.length;
  return { esperadas, pickeadas, completo: esperadas > 0 && pickeadas >= esperadas };
}

export interface VistaArticulo {
  grupoKey: string;
  nombre: string | null;
  sku: string | null;
  gtin: string | null;
  pickeadas: number;
  esperadas: number;
  /** Series pickeadas en orden. */
  series: string[];
}

export interface VistaLote {
  lote: string;
  /** `MM/YYYY` para mostrar; del ERP si lo tiene, si no del DataMatrix. */
  vencimiento: string | null;
  pickeadas: number;
  esperadas: number;
  articulos: VistaArticulo[];
}

function vencimientoErpAMesAnio(value: string | null): string | null {
  const m = /^(\d{4})-(\d{2})/.exec(value ?? '');
  const yyyy = m?.[1];
  const mm = m?.[2];
  return yyyy && mm ? `${mm}/${yyyy}` : null;
}

/**
 * Lista que ve el operador: se genera a partir de lo pickeado, en orden de
 * primer pickeo. Encabezado por lote con la cantidad acumulada, y debajo una
 * fila por artículo de ese lote.
 */
export function vistaLotes(state: PickeoLotesState, grupos: readonly GrupoEsperado[]): VistaLote[] {
  const porKey = new Map(grupos.map((g) => [g.key, g]));
  const lotes = new Map<string, VistaLote>();
  for (const p of state.pickeos) {
    const g = porKey.get(p.grupoKey);
    if (!g) continue;
    let vl = lotes.get(g.lote);
    if (!vl) {
      vl = {
        lote: g.lote,
        vencimiento:
          vencimientoErpAMesAnio(g.vencimiento) ?? vencimientoGs1AMesAnio(p.lectura.vencimiento),
        pickeadas: 0,
        esperadas: grupos.filter((x) => x.lote === g.lote).reduce((s, x) => s + x.cantidad, 0),
        articulos: [],
      };
      lotes.set(g.lote, vl);
    }
    vl.pickeadas += 1;
    let art = vl.articulos.find((a) => a.grupoKey === g.key);
    if (!art) {
      art = {
        grupoKey: g.key,
        nombre: g.nombre,
        sku: g.sku,
        gtin: g.gtin,
        pickeadas: 0,
        esperadas: g.cantidad,
        series: [],
      };
      vl.articulos.push(art);
    }
    art.pickeadas += 1;
    art.series.push(p.serie);
  }
  return Array.from(lotes.values());
}
